<template>
  <div class="card">
    <div class="bg-blue-light px-1 rounded pb-2">
      <TitleButton
          class="mt-1"
          btnTitle="List"
          :showBtn="true"
          :showAddNew="false"
          title="Add PR Stock Transfer"
          @onClickCloseButton="navigateToListPage"
      />

      <div class="row mt-2 gy-1">
        <div class="row mt-1">
          <div class="col-sm-4">
            <label class="align-middle">Transfer From</label>
            <v-select
                placeholder="Select Warehouse"
                v-model="formData.from_id"
                :options="warehouses"
                label="text"
                :reduce="text => text.id"
                :disabled="true"
            />
          </div>
          <div class="col-sm-4"></div>
          <div class="col-sm-4">
            <label class="align-middle">Transfer To</label>
            <v-select
                placeholder="Select Warehouse"
                v-model="formData.to_id"
                :options="warehouses"
                label="text"
                :reduce="text => text.id"
            />
          </div>
        </div>
        <div class="row mt-1">
          <div class="col-sm-4">
            <label class="align-middle">Transfer Date</label>
            <input type="date" class="form-control" v-model="formData.date">
          </div>
          <div class="col-sm-4"></div>
          <div class="col-sm-4">
            <label class="align-middle">Transfer No</label>
            <input type="text" class="form-control" v-model="formData.transfer_no">
          </div>
        </div>
      </div>
    </div>

    <div class="px-2 mt-2">
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Product</th>
            <th>Quantity</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in formData.item_details" :key="index">
            <td>
              <input type="text" class="form-control" :value="item.product_name" readonly>
            </td>
            <td>
              <input type="number" class="form-control" v-model="item.transfer_qty" placeholder="Quantity">
            </td>
            <td>
              <input type="text" class="form-control" v-model="item.note" placeholder="Note">
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="p-1">
      <div class="row mt-3 px-2">
        <div class="col-12">
          <div class="mb-2">
            <label for="note" class="form-label fw-bold">Note</label>
            <textarea v-model="formData.note" placeholder="Note" class="form-control" rows="2" id="note"></textarea>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-2 d-flex justify-content-start justify-content-sm-center px-2 gap-1 flex-wrap pb-2">
      <hr class="hr-full-width mb-1">
      <button :disabled="saveButtonLoader || saveNewButtonLoader" @click="handleSubmit(true)"
              class="btn btn-primary">
        Save
      </button>
      <button :disabled="saveButtonLoader || saveNewButtonLoader" @click="handleSubmit(false)"
              class="btn btn-primary">Save & New
      </button>
      <button @click="navigateToListPage" class="btn btn-outline-secondary">Cancel</button>
    </div>

    <GlobalLoader/>
  </div>
</template>
<script setup>
import {computed, inject, onMounted, ref, watch} from 'vue'
import {useRoute, useRouter} from "vue-router";
import TitleButton from '@/components/atom/TitleButton'
import {useStore} from "vuex";
import handleBusinessesLocations from "@/services/modules/businessesLocations";
import handleStock from "@/services/modules/stock";
import handleRequisitions from "@/services/modules/procurement/requisition";

const $router = useRouter()
const $route = useRoute()
const $store = useStore()
const showError = inject('showError');
const showSuccess = inject('showSuccess')
const {fetchBusinessLocationsList} = handleBusinessesLocations();
const {fetchStockTransferNumber, storeStockTransfer} = handleStock();
const {fetchRequisitionGeneralByLocation} = handleRequisitions();

let saveButtonLoader = ref(false)
let saveNewButtonLoader = ref(false)
let warehouses = ref([])
let locationId = ref(null)

let formData = ref({
  from_id: null,
  to_id: null,
  date: null,
  transfer_no: null,
  note: null,
  item_details: [],
})

const navigateToListPage = () => {
  $router.push({name: `pr-stock-transfer-list`, params: $route.params, query: $route.query});
}

const resetForm = () => {
    formData.value = {
        from_id: null,
        to_id: null,
        date: null,
        transfer_no: null,
        note: null,
        item_details: [],
    }
}

const handleSubmit = (redirect=false) => {
  $store.state.loading = true;
  let query = `?company_id=${$route.params.companyId}`;
  redirect ? saveButtonLoader.value = true : saveNewButtonLoader.value = true;
  if(!formData.value.date) formData.value.date = new Date().toISOString().split('T')[0]
  let copyFormData = { ...formData.value }
  copyFormData.item_details = JSON.stringify(formData.value.item_details)
  storeStockTransfer(query, copyFormData)
    .then(res => {
      saveButtonLoader.value = false
      saveNewButtonLoader.value = false

      if(res.status) {
          showSuccess(res.message)
          if(redirect) navigateToListPage()
          resetForm()
      }
      if(!res.status) {
          showError(res.message)
      }
  })
  .catch(err=> {
      saveButtonLoader.value = false;
      saveNewButtonLoader.value = false;
      $store.state.loading = false;
      showError(err)
  })
}

// Get requisition generals by location
const getRequisitionGeneralByLocation = () => {
    let query = `?company_id=${$route.params.companyId}&location_id=${locationId.value}`;
    fetchRequisitionGeneralByLocation(query)
      .then(res => {
          if (res.status && res.data && res.data.length > 0) {
              // Transform the data to item_details format
              const itemDetails = res.data.map(item => ({
                  requisition_general_ids: item.requisition_general_ids,
                  product_name: item.product_name,
                  product_id: item.product_id,
                  transfer_qty: item.quantity || null,
                  note: item.note || null
              }));
              
              formData.value.item_details = itemDetails;
          }
      })
    .catch(err => {
        showError('Failed to load requisition data');
    });
};

onMounted(  () => {
    let query = `?company_id=${$route.params.companyId}`;
    formData.value.date = new Date().toISOString().split('T')[0]
    locationId.value = $route.params.locationId;
    formData.value.from_id = locationId.value ? parseInt(locationId.value) : null;
    
    Promise.all([
        fetchBusinessLocationsList(query).then(res => {
            if (res.data) {
                warehouses.value = res.data;
            }
        }),
        fetchStockTransferNumber(query).then( res => {
            if (res.data) {
                formData.value.transfer_no = res.data;
            }
        })
    ]).finally(() => {
        if (locationId.value) {
            getRequisitionGeneralByLocation();
        }
    });
})
</script>
